import { Col, Row } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import type { TerminalToCampaignEntity } from "src/app/features/Campaign/types/TerminalToCampaignEntity";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import type { ID } from "../../../../core/types/fe";
import type { ExtendedColumnsType } from "../../../index";
import { MultiSegmentProgressBar } from "../../../ProgressBar/MultiSegmentProgressBar";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { displayDateTimeValue, displayEnumValue } from "../../../Table/components/PropertyTable/transformations/cellTransformations";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";
import { TextInput } from "../Text/TextInput";
import { useLoadCampaignTerminalsQuery } from "./serverApi";

export interface TerminalsToCampaignsProps extends AntCustomFormField<TerminalToCampaignEntity[]>, CommonFieldAttributes {
    campaignId: ID;
}

export const TerminalsToCampaignTable: FC<TerminalsToCampaignsProps> = ({ value, campaignId }) => {
    const [allTerminals, setAllTerminals] = useState<TerminalToCampaignEntity[]>([]);
    const [filter, setFilter] = useState<string>();
    const [refreshIndex, setRefreshIndex] = useState(0);

    const getColumnsConfig = useCallback((): ExtendedColumnsType<TerminalToCampaignEntity>[] => {
        return [
            {
                title: i18n.t("terminal.id"),
                key: "id",
                dataIndex: "id",
            },
            {
                title: i18n.t("terminal.terminalId"),
                key: "terminalId",
                dataIndex: "terminalId",
            },
            {
                title: i18n.t("terminal.name"),
                key: "name",
                dataIndex: "terminalName",
            },
            {
                title: i18n.t("campaign.export.applicationVersion"),
                key: "appVersion",
                dataIndex: "appVersion",
            },
            {
                title: i18n.t("campaign.export.configurationVersion"),
                key: "confVersion",
                dataIndex: "confVersion",
            },
            {
                title: i18n.t("terminal.status"),
                key: "updateStatus",
                dataIndex: "updateStatus",
                render: (cellValue) => displayEnumValue(cellValue, "campaign-update-status"),
            },
            {
                title: i18n.t("terminal.changed"),
                key: "changed",
                dataIndex: "changed",
                render: (cellValue) => displayDateTimeValue(cellValue),
            },
        ];
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setRefreshIndex((prev) => prev + 1);
        }, 15000); // 15 seconds

        return () => clearInterval(interval);
    }, []);

    const request = {
        dataGroupCode: getAssignedDataGroup()?.code,
        campaignId,
        refreshIndex,
    };

    const { data } = useLoadCampaignTerminalsQuery(request, { refetchOnMountOrArgChange: true, refetchOnReconnect: true });

    const valueToString = (v: unknown): string => {
        if (v == null) return "";
        return String(v).toLowerCase();
    };

    const filteredTerminals = useMemo(() => {
        if (!filter) return allTerminals;
        const f = filter.toLowerCase();
        return allTerminals.filter((terminal) => {
            return [
                terminal.id,
                terminal.terminalId,
                terminal.terminalName,
                terminal.appVersion,
                terminal.confVersion,
                terminal.updateStatus,
                terminal.changed,
            ].some((v) => valueToString(v).includes(f));
        });
    }, [allTerminals, filter]);

    const navigate = useNavigate();
    const onRowClick = (record: TerminalToCampaignEntity) => {
        navigate(`${BASE_PATH}/${ResourceName.TERMINALS}/${FORM_ACTION.EDIT}/${record.terminalSystemId}`);
    };

    return (
        <>
            <Row key={`searchTerminalsTextInput`} style={{ display: "flex", justifyContent: "flex-end" }}>
                <Col span={10} key={`searchTerminalsTextInputCol`}>
                    <TextInput value={filter} onChange={setFilter} placeholder={"Search"} />
                </Col>
            </Row>
            <MultiSegmentProgressBar value={value} data={data} setTerminals={setAllTerminals} refreshIndex={refreshIndex} />
            {filteredTerminals.length !== 0 && (
                <PropertyTable<TerminalToCampaignEntity>
                    getColumnsConfig={getColumnsConfig}
                    tableData={filteredTerminals}
                    propertyAsRowKey="id"
                    readOnly
                    rowsAreSelectable={false}
                    onRow={(record: TerminalToCampaignEntity) => {
                        return {
                            onClick: () => onRowClick(record),
                        };
                    }}
                />
            )}
            {allTerminals.length === 0 && <label>&nbsp;{i18n.t("campaignCreate.affectedTerminalsZero")}</label>}
        </>
    );
};
